import React, { useEffect, useState } from "react";
import { api } from "../../lib/nurseryApi";
import { Megaphone, ChatCircleDots, Envelope, Buildings, Newspaper } from "@phosphor-icons/react";
import BackToOverview from "../../components/BackToOverview";

export default function AdminMarketing() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/marketing").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="fade-up">
        <BackToOverview />
        <h1 className="page-title">Marketing</h1>
        <div className="card" style={{ padding: 24, marginTop: 16 }}>Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-marketing-page" className="fade-up">
      <BackToOverview />
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Top of funnel</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>Marketing</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>Where your nurseries and leads are coming from — and where to double down.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 24 }}>
        <KPI icon={Envelope} label="Total applications" value={data.applications_total} hint="All-time field-test applicants" />
        <KPI icon={Buildings} label="Approved nurseries" value={data.approved_total} hint="Live accounts on the platform" />
        <KPI icon={ChatCircleDots} label="Leads captured" value={data.leads_total} hint="From all nursery chatbots & forms" />
        <KPI icon={Newspaper} label="Blog posts live" value={data.blog_published} hint="SEO + thought leadership" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginTop: 32 }}>
        <div className="card" style={{ padding: 22 }} data-testid="referral-channels-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Megaphone size={20} weight="bold" color="var(--secondary)" />
            <h3 className="font-display" style={{ fontSize: 20 }}>Application referral channels</h3>
          </div>
          <p style={{ color: "var(--muted-fg)", fontSize: 13, marginTop: 4 }}>How approved & pending applicants found you.</p>
          {data.referral_breakdown.length === 0 ? (
            <div style={{ marginTop: 14, padding: 14, background: "var(--bg-alt)", borderRadius: 12, fontSize: 13, color: "var(--muted-fg)" }}>
              No applications yet. Channels will appear here as nurseries apply.
            </div>
          ) : (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {data.referral_breakdown.map((r) => (
                <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 14 }}>{r.label}</div>
                  <div style={{ flex: 2, height: 8, background: "var(--bg-alt)", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${r.pct}%`, background: "var(--primary)" }} />
                  </div>
                  <div style={{ width: 60, textAlign: "right", fontWeight: 700, fontSize: 13 }}>{r.count} <span style={{ color: "var(--muted-fg)", fontWeight: 400 }}>({r.pct}%)</span></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 22 }} data-testid="lead-sources-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ChatCircleDots size={20} weight="bold" color="var(--primary)" />
            <h3 className="font-display" style={{ fontSize: 20 }}>Lead source mix</h3>
          </div>
          <p style={{ color: "var(--muted-fg)", fontSize: 13, marginTop: 4 }}>Where customer leads enter the platform.</p>
          {data.lead_source_breakdown.length === 0 ? (
            <div style={{ marginTop: 14, padding: 14, background: "var(--bg-alt)", borderRadius: 12, fontSize: 13, color: "var(--muted-fg)" }}>
              No leads captured yet.
            </div>
          ) : (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {data.lead_source_breakdown.map((r) => (
                <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 14 }}>{r.label}</div>
                  <div style={{ flex: 2, height: 8, background: "var(--bg-alt)", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${r.pct}%`, background: "var(--secondary)" }} />
                  </div>
                  <div style={{ width: 60, textAlign: "right", fontWeight: 700, fontSize: 13 }}>{r.count} <span style={{ color: "var(--muted-fg)", fontWeight: 400 }}>({r.pct}%)</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card-soft" style={{ marginTop: 24, padding: 16, background: "var(--accent-soft)", borderRadius: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#8B6E1A", letterSpacing: "0.1em", textTransform: "uppercase" }}>Coming soon</div>
        <div style={{ fontSize: 13.5, color: "var(--text)", marginTop: 6, lineHeight: 1.5 }}>
          Email campaigns, UTM tracking, and promo codes will plug in once your Hostinger SMTP is connected.
          Until then, the channel + source mix above is computed live from real applications and leads.
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, hint }) {
  return (
    <div className="stat-tile">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)" }}>{label}</div>
        <div style={{ width: 38, height: 38, borderRadius: 12, background: "var(--bg-alt)", display: "grid", placeItems: "center" }}>
          <Icon size={20} weight="bold" color="var(--secondary)" />
        </div>
      </div>
      <div className="font-display" style={{ fontSize: 36, marginTop: 10, lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--muted-fg)" }}>{hint}</div>}
    </div>
  );
}
